import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useAdminPermit } from "@/hooks/use-admin-permits";
import { StatusBadge } from "@/components/status-badge";
import { formatDate } from "@njbsoft/utils";

export const Route = createFileRoute("/_admin/permits/$permitId")({
  component: AdminPermitDetailPage,
});

const STATUS_COLORS: Record<string, string> = {
  active: "bg-green-100 text-green-800",
  draft: "bg-gray-100 text-gray-700",
  expired: "bg-red-100 text-red-800",
  terminated: "bg-red-100 text-red-800",
  revoked: "bg-red-100 text-red-800",
  administratively_continued: "bg-yellow-100 text-yellow-800",
};

const NPDES_TYPES = [
  "npdes",
  "pretreatment",
  "state",
  "stormwater_ms4",
  "stormwater_msgp",
  "caa",
  "sdwa",
];
const DEVICE_TYPES = ["fog", "cross_connection"];

function AdminPermitDetailPage() {
  const { permitId } = Route.useParams();
  const { data: permit, isLoading, error } = useAdminPermit(permitId);
  const { t } = useTranslation("permits");

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-6 w-24 animate-pulse rounded bg-gray-200" />
        <div className="h-64 animate-pulse rounded-xl bg-gray-100" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
        {t("adminDetail.loadError", { message: error.message })}
      </div>
    );
  }

  if (!permit) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-gray-600">
        {t("adminDetail.notFound")}
      </div>
    );
  }

  const isNpdesType = NPDES_TYPES.includes(permit.permit_type);
  const isDeviceType = DEVICE_TYPES.includes(permit.permit_type);

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="text-sm text-gray-500">
        <Link to="/permits" className="hover:text-primary">
          {t("title")}
        </Link>
        <span className="mx-2">/</span>
        <span className="text-gray-900">{permit.permit_number}</span>
      </nav>

      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">{permit.permit_number}</h1>
        <span
          className={`inline-flex items-center rounded-full px-3 py-1 text-sm font-medium ${
            STATUS_COLORS[permit.status] ?? "bg-gray-100 text-gray-700"
          }`}
        >
          {t(`statuses.${permit.status}`, { defaultValue: permit.status.replace(/_/g, " ") })}
        </span>
      </div>

      {/* Overview Card */}
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <h2 className="mb-4 text-lg font-semibold text-gray-900">{t("tabs.overview")}</h2>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
          <DetailField label={t("adminDetail.organization")} value={permit.org_name} />
          <DetailField label={t("fields.facility")} value={permit.facility_name} />
          <DetailField
            label={t("fields.permitType")}
            value={
              <StatusBadge
                status={t(`types.${permit.permit_type}`, { defaultValue: permit.permit_type })}
              />
            }
          />
          <DetailField label={t("fields.issuingAgency")} value={permit.issuing_agency} />
          <DetailField
            label={t("fields.issuedDate")}
            value={permit.issued_date ? formatDate(permit.issued_date) : "-"}
          />
          <DetailField
            label={t("fields.effectiveDate")}
            value={permit.effective_date ? formatDate(permit.effective_date) : "-"}
          />
          <DetailField
            label={t("fields.expirationDate")}
            value={permit.expiration_date ? formatDate(permit.expiration_date) : "-"}
          />
          <DetailField label={t("fields.createdAt")} value={formatDate(permit.created_at)} />
        </dl>
        {permit.description ? (
          <div className="mt-4 border-t border-gray-100 pt-4">
            <dt className="text-sm font-medium text-gray-500">{t("fields.description")}</dt>
            <dd className="mt-1 text-sm text-gray-900">{permit.description}</dd>
          </div>
        ) : null}
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4">
        <StatCard label={t("adminDetail.parameters")} value={permit.parameter_count} />
        <StatCard label={t("adminDetail.limits")} value={permit.limit_count} />
        <StatCard label={t("adminDetail.outfalls")} value={permit.outfall_count} />
      </div>

      {/* NPDES info */}
      {isNpdesType ? (
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          <h2 className="text-lg font-semibold text-gray-900">
            {t("adminDetail.parametersAndLimits")}
          </h2>
          <p className="mt-2 text-sm text-gray-500">
            {t("adminDetail.parametersLimitsDescription", {
              paramCount: permit.parameter_count,
              limitCount: permit.limit_count,
              outfallCount: permit.outfall_count,
            })}
          </p>
        </div>
      ) : null}

      {/* Schedules info */}
      {isNpdesType ? (
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          <h2 className="text-lg font-semibold text-gray-900">
            {t("adminDetail.monitoringSchedules")}
          </h2>
          <div className="mt-2 flex items-center gap-2">
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                permit.parameter_count > 0
                  ? "bg-blue-100 text-blue-800"
                  : "bg-gray-100 text-gray-600"
              }`}
            >
              {permit.parameter_count > 0
                ? t("adminDetail.schedulesAvailable")
                : t("adminDetail.schedulesNone")}
            </span>
            <span className="text-sm text-gray-500">
              {permit.parameter_count > 0
                ? t("adminDetail.schedulesDescription", { count: permit.parameter_count })
                : t("adminDetail.schedulesEmpty")}
            </span>
          </div>
        </div>
      ) : null}

      {/* Device tracking for FOG/cross_connection */}
      {isDeviceType ? (
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          <h2 className="text-lg font-semibold text-gray-900">{t("adminDetail.deviceTracking")}</h2>
          <p className="mt-2 text-sm text-gray-500">{t("adminDetail.deviceTrackingDescription")}</p>
        </div>
      ) : null}
    </div>
  );
}

function DetailField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-sm font-medium text-gray-500">{label}</dt>
      <dd className="mt-1 text-sm text-gray-900">{value ?? "-"}</dd>
    </div>
  );
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 text-center">
      <div className="text-2xl font-bold text-gray-900">{value}</div>
      <div className="text-sm text-gray-500">{label}</div>
    </div>
  );
}
